import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, MoonIcon } from 'lucide-react';
import { ChatActionType } from '@cotera/sdk/core';
import type { ToolRendererProps } from './index';
import { translateToolName } from '../../../app/v1/components/translate-tool-name';
import { useWatchableValue, Watchable } from '../../actions/framework';

const COCO_SLEEP_MAX_MS = 24 * 60 * 60 * 1000;

type CocoSleepInput = {
  seconds?: number | null;
  minutes?: number | null;
  hours?: number | null;
  reason?: string | null;
};

type CocoSleepOutput = {
  sleptForMs?: number;
  sleptForSeconds?: number;
  reason?: string | null;
};

/** Mirrors server {@link resolveCocoSleepMs} for UI progress timing. */
export const resolveSleepDurationMs = (input: CocoSleepInput): number => {
  const seconds = input.seconds ?? 0;
  const minutes = input.minutes ?? 0;
  const hours = input.hours ?? 0;
  const requestedMs = (hours * 3600 + minutes * 60 + seconds) * 1000;
  if (!Number.isFinite(requestedMs)) {
    return 0;
  }
  return Math.min(Math.max(Math.round(requestedMs), 0), COCO_SLEEP_MAX_MS);
};

export const formatSleepDuration = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (remMinutes > 0) {
    return `${hours}h ${remMinutes}m`;
  }
  return `${hours}h`;
};

const UNDEFINED_CONVERSATION_WATCHABLE = Watchable.fromValue(null);

function getInterruptedToolStartMs(
  conversation: ReturnType<
    NonNullable<
      ToolRendererProps['chatModel']['conversationWatchable']
    >['snapshot']
  >,
  toolCallId: string
): number | undefined {
  if (conversation === null || conversation === undefined) {
    return undefined;
  }
  const actions = conversation.getActions({
    type: ChatActionType.InterruptedToolInput,
  });
  for (const action of actions) {
    const data = action.data;
    if (data?.['toolCallId'] === toolCallId) {
      return action.createdAt.getTime();
    }
  }
  return undefined;
}

function useSleepProgress({
  totalMs,
  startMs,
  running,
}: {
  totalMs: number;
  startMs: number | undefined;
  running: boolean;
}): { progress: number; remainingMs: number } {
  const mountStartMs = useRef(Date.now()).current;
  const effectiveStartMs = startMs ?? mountStartMs;
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (running === false || totalMs <= 0) {
      return;
    }
    let rafId = 0;
    const tick = () => {
      setNowMs(Date.now());
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [running, totalMs]);

  const elapsedMs = Math.max(0, nowMs - effectiveStartMs);
  const remainingMs = Math.max(0, totalMs - elapsedMs);
  const progress =
    totalMs <= 0
      ? 100
      : running
      ? Math.min(99, (elapsedMs / totalMs) * 100)
      : 100;

  return { progress, remainingMs };
}

const SleepProgressBar: React.FC<{
  progress: number;
  running: boolean;
}> = ({ progress, running }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-primary/20">
    <div
      className="h-full w-full origin-left rounded-full bg-primary will-change-transform"
      style={{
        transform: `scaleX(${progress / 100})`,
        transition: running
          ? undefined
          : 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    />
  </div>
);

export const SleepToolRenderer: React.FC<ToolRendererProps> = ({
  part,
  chatModel,
}) => {
  const running = part.isIncomplete();
  const input = useMemo(() => part.input ?? {}, [part.input]);
  const output = part.getOutput<CocoSleepOutput>();
  const totalMs = useMemo(() => resolveSleepDurationMs(input), [input]);
  const sleptMs =
    typeof output?.sleptForMs === 'number'
      ? output.sleptForMs
      : typeof output?.sleptForSeconds === 'number'
      ? output.sleptForSeconds * 1000
      : totalMs;

  const conversationWatchable =
    chatModel.conversationWatchable ?? UNDEFINED_CONVERSATION_WATCHABLE;
  const conversation = useWatchableValue(conversationWatchable);
  const interruptedStartMs = useMemo(
    () => getInterruptedToolStartMs(conversation, part.toolCallId),
    [conversation, part.toolCallId]
  );

  const { progress, remainingMs } = useSleepProgress({
    totalMs,
    startMs: interruptedStartMs,
    running,
  });

  const reason =
    typeof input['reason'] === 'string' && input['reason']?.trim().length > 0
      ? input['reason']?.trim()
      : typeof output?.reason === 'string' && output.reason?.trim().length > 0
      ? output.reason?.trim()
      : null;

  const title = translateToolName(
    part.toolKey().toString(),
    running ? 'running' : 'done'
  );

  if (totalMs <= 0 && running === false) {
    return (
      <div className="flex items-center gap-2 py-1 text-sm italic text-muted-foreground">
        <CheckIcon className="h-4 w-4 text-success" />
        <span>Resumed immediately</span>
      </div>
    );
  }

  return (
    <div className="flex max-w-[60%] flex-col gap-2 py-1 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        {running ? (
          <MoonIcon className="h-4 w-4 shrink-0 animate-pulse" />
        ) : (
          <CheckIcon className="h-4 w-4 shrink-0 text-success" />
        )}
        <span className="italic">{title}</span>
      </div>
      {reason !== null ? (
        <p className="text-muted-foreground">{reason}</p>
      ) : null}
      <SleepProgressBar progress={running ? progress : 100} running={running} />
      <div className="text-xs text-muted-foreground transition-opacity duration-300">
        {running
          ? `${formatSleepDuration(remainingMs)} remaining`
          : `Paused for ${formatSleepDuration(sleptMs)}`}
      </div>
    </div>
  );
};
